import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, tap } from 'rxjs';

import { User } from '../models';
import userPermissions from './user-permissions';

const userSubject$: BehaviorSubject<User> = new BehaviorSubject<User>(null);
const tenant$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

@Injectable({
    providedIn: 'root',
})
export class UserService {
    API_URL = environment.API_URL;
    PERMISSIONS: {
        [key: string]: { name: string; code: string }[]; // <-- añade esto
        HOME: { name: string; code: string }[];
        POS: { name: string; code: string }[];
        INVENTORY: { name: string; code: string }[];
        BANK_ACCOUNTS: { name: string; code: string }[];
        COMMERCE: { name: string; code: string }[];
        FINANCIAL_SUMMARY: { name: string; code: string }[];
        FIXED_AND_VARIABLE_EXPENSES: { name: string; code: string }[];
        FISCAL_INVOICE: { name: string; code: string }[];
        MARKETING: { name: string; code: string }[];
        STATISTICS: { name: string; code: string }[];
    } = { ...userPermissions };

    constructor(private http: HttpClient) {
        // this.user = {
        //     id: '123',
        //     firstName: 'Valerie',
        //     lastName: 'Luna',
        //     email: 'vluna@aol.com',
        //     image: '/assets/img/illustrations/profiles/profile-1.png',
        // };
    }

    set user(user: User) {
        userSubject$.next(user);
    }

    get user$() {
        return userSubject$;
    }

    set tenant(tenant: any) {
        tenant$.next(tenant);
    }

    get tenant$() {
        return tenant$;
    }

    getTenant$() {
        return this.http.get<any>(`${this.API_URL}/tenants/tenantId`);
    }

    getLoginQuantity$(): Observable<any> {
        return this.http.get<any>(`${this.API_URL}/tenants/loginQuantity`);
    }

    retrieveUserData() {
        return this.http.get<User>(`${this.API_URL}/users/${userSubject$.getValue()._id}`).subscribe({
            next: user => {
                let newUser = new User(
                    user._id,
                    user.email,
                    user.name,
                    user.lastName,
                    user.tenantId,
                    userSubject$.getValue().token,
                    userSubject$.getValue().tokenExpirationDate,
                    user.roles,
                    user.phone,
                    user.birthday,
                    user.location,
                    user.locations,
                    user.enabled,
                    user.status,
                    user.permissions,
                    user.showNews,
                    user.instructionsNotToShow,
                    user.salePreferences,
                    !!user.creditAccess,
                    !!user.isOwner,
                    // GET /users/:id usa userMapping y no devuelve instructionsViews: conservar el de la sesión.
                    user.instructionsViews ?? userSubject$.getValue()?.instructionsViews ?? {},
                );
                this.user = newUser;
            },
        });
    }

    refreshUserData(): Observable<any> {
        const currentUser = userSubject$.getValue();

        return this.http.post<any>(`${this.API_URL}/auth/verified-user`, {}).pipe(
            tap(res => {
                if (res && res.data) {
                    const backendData = res.data;
                    const updatedUser = Object.assign(Object.create(Object.getPrototypeOf(currentUser)), currentUser, backendData);
                    this.user = updatedUser;
                    localStorage.setItem('userData', JSON.stringify(updatedUser));
                }
            }),
        );
    }

    createNewUser(newUser: any) {
        return this.http.post<User>(`${this.API_URL}/users`, newUser);
    }

    /**
     * `PUT /users/:id` (Mi cuenta). `currentPassword` solo viaja cuando cambia el teléfono (ADM-813): `api`
     * lo exige en ese caso. Quien llama decide si el body lleva `phone` (sin `phone` no es un cambio).
     */
    updateUser(user: Partial<User>, currentPassword?: string) {
        const body = currentPassword ? { ...user, currentPassword } : user;
        return this.http.put<User>(`${this.API_URL}/users/${userSubject$.getValue()._id}`, body);
    }

    /**
     * Único punto que parchea el usuario de la sesión (ADM-813): clona con el prototipo de `User` (conserva el
     * token, `creditAccess`, `isOwner` y `roles`), publica en `user$` y persiste en `localStorage['userData']`.
     * Sin usuario en sesión no hace nada.
     */
    patchSessionUser(patch: Partial<User>): void {
        const current = userSubject$.getValue();
        if (!current) return;
        const updated: User = Object.assign(Object.create(User.prototype), current, patch);
        this.user = updated;
        localStorage.setItem('userData', JSON.stringify(updated));
    }

    updateUserData(user: User, id: string) {
        return this.http.put<User>(`${this.API_URL}/users/associated-user/${id}`, user);
    }

    getUsersAssociated$() {
        return this.http.get<any>(`${this.API_URL}/users`);
    }

    getUsersLimitStatus$() {
        return this.http.get<any>(`${this.API_URL}/users/limit-status`);
    }

    deleteAssociatedUser$(associatedUserId: string) {
        return this.http.delete<any>(`${this.API_URL}/users/${associatedUserId}`);
    }

    changeUserEnablement$(associatedUserId: string, status: string) {
        return this.http.put<any>(`${this.API_URL}/users/changeEnablement/${associatedUserId}`, { status });
    }

    updateSalePreferences(preference: string, value: boolean | string | string[] | Record<string, boolean>) {
        return this.http
            .put<any>(`${this.API_URL}/users/update-sale-preferences/${userSubject$.getValue()._id}`, {
                salePreferences: { [preference]: value },
            })
            .pipe(
                tap(response => {
                    const user = this.user$.getValue();
                    user.salePreferences = response.data.salePreferences;
                    this.user = user;
                    // Also update localStorage so preferences persist on page refresh
                    const userData = JSON.parse(localStorage.getItem('userData') || '{}');
                    if (userData) {
                        userData.salePreferences = response.data.salePreferences;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                }),
            )
            .subscribe({
                next: response => console.log('Request successful', response),
                error: error => console.error('Request failed', error),
            });
    }

    updateMultipleSalePreferences(preferences: Record<string, boolean | string>) {
        return this.http
            .put<any>(`${this.API_URL}/users/update-sale-preferences/${userSubject$.getValue()._id}`, {
                salePreferences: preferences,
            })
            .pipe(
                tap(response => {
                    const user = this.user$.getValue();
                    user.salePreferences = response.data.salePreferences;
                    this.user = user;
                }),
            )
            .subscribe({
                next: response => console.log('Request successful', response),
                error: error => console.error('Request failed', error),
            });
    }
}
